import { Controller } from "@hotwired/stimulus";

// Syncs the stepper UI and order summary visibility when
// the checkout Turbo Frame loads a new step.
export default class extends Controller {
  static targets = ["email", "verifiedBadge", "reverifyHint"]
  connect() {
    this.sync();
  }

  // Called by Turbo's frame-load event on the checkout-step frame.
  // Wire up: data-action="turbo:frame-load@document->checkout#onStepLoad"
  onStepLoad(event) {
    const frame = document.getElementById("checkout-step");
    if (!frame || event.target !== frame) return;
    this.sync();
  }

  sync() {
    const frame = document.getElementById("checkout-step");
    if (!frame) return;

    const stepEl = frame.querySelector("[data-checkout-step-step-value]");
    if (!stepEl) return;

    const step = parseInt(stepEl.dataset.checkoutStepStepValue || "1", 10);
    this.updateStepper(step);

    this.emailChanged();
  }

  updateStepper(current) {
    document.querySelectorAll("[data-step-id]").forEach((el) => {
      const id = parseInt(el.dataset.stepId, 10);
      const circle = el.querySelector("[data-step-circle]");
      const connector = el.querySelector("[data-step-connector]");

      if (!circle) return;

      circle.classList.remove(
        "bg-emerald",
        "text-white",
        "bg-navy",
        "ring-4",
        "ring-navy/10",
        "bg-light",
        "text-muted-foreground",
      );

      if (id < current) {
        circle.classList.add("bg-emerald", "text-white");
        circle.innerHTML = `<svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>`;
      } else if (id === current) {
        circle.classList.add("bg-navy", "text-white", "ring-4", "ring-navy/10");
        circle.textContent = id;
      } else {
        circle.classList.add("bg-light", "text-muted-foreground");
        circle.textContent = id;
      }

      const text = el.querySelector("[data-step-text]");
      if (text) {
        text.classList.remove("text-navy", "text-muted-foreground");
        if (id <= current) {
          text.classList.add("text-navy");
        } else {
          text.classList.add("text-muted-foreground");
        }
      }

      if (connector) {
        connector.classList.toggle("bg-emerald", id < current);
        connector.classList.toggle("bg-light", id >= current);
      }
    });
  }

  emailChanged() {
    if (!this.hasEmailTarget) return
    if (!this.hasVerifiedBadgeTarget) return

    const verifiedEmail = (this.emailTarget.dataset.verifiedEmail || "").trim().toLowerCase();
    const current = this.emailTarget.value.trim().toLowerCase();

    if (!verifiedEmail) {
      this.verifiedBadgeTarget.classList.add("hidden");
      if (this.hasReverifyHintTarget) {
        this.reverifyHintTarget.classList.add("hidden");
      }
      return;
    }

    const isVerified = (verifiedEmail === current);
    this.verifiedBadgeTarget.classList.toggle("hidden", !isVerified);

    if (this.hasReverifyHintTarget) {
      this.reverifyHintTarget.classList.toggle("hidden", isVerified);
    }
  }

  confirmEmailChange(event) {
    if (!this.hasEmailTarget) return

    const verifiedEmail = (this.emailTarget.dataset.verifiedEmail || "").trim().toLowerCase()
    const current = this.emailTarget.value.trim().toLowerCase()

    // No previously-verified email, or email hasn't changed — let it submit normally
    if (!verifiedEmail || verifiedEmail === current) return

    // Already confirmed via the modal on a previous attempt — let it through
    if (event.target.dataset.emailChangeConfirmed === "true") return

    event.preventDefault()
    event.stopImmediatePropagation()

    const modal = document.getElementById("email-change-confirm-modal")
    if (!modal) return // fail-open if modal markup isn't present

    this.pendingForm = event.target
    modal.showModal()
  }

  confirmSubmit() {
    const modal = document.getElementById("email-change-confirm-modal")
    if (modal) modal.close()

    if (this.pendingForm) {
      this.pendingForm.dataset.emailChangeConfirmed = "true"
      this.pendingForm.requestSubmit()
      this.pendingForm = null
    }
  }

  cancelSubmit() {
    const modal = document.getElementById("email-change-confirm-modal")
    if (modal) modal.close()
    this.pendingForm = null
  }
}
